import { BarChart3, CheckSquare, Coins, NewspaperIcon } from 'lucide-react';
import { useRouter } from 'next/router';
import { useDialog } from '@/code/frontend/components/hooks/use-dialog';
import { usePostManagerAccess } from '@/code/frontend/components/posts/shared/postManagerAccess';
import { ReferralPage } from '@/code/frontend/components/ReferralPage';
import { Button } from '@/code/frontend/components/ui/button';
import { NavigationMenu } from '@/code/frontend/components/ui/navigation-menu/navigation-menu';
import { LinkRouter } from '@/code/lib/routes';
import { trpc } from '@/code/lib/utils/trpc';

export const DashboardNavigation = ({
  useDashboardTopBar,
  forceShowNewCampaignButton,
  dbUser,
}: {
  forceShowNewCampaignButton?: boolean;
  useDashboardTopBar: boolean;
  dbUser: { id: string } | null;
}) => {
  const router = useRouter();
  const referralDialog = useDialog(ReferralPage);

  const useCanSendReferrals =
    trpc.featureFlags.getIsFlagEnabledForUser.useQuery('CAN_SEND_REFERRALS');

  const postManagerAccess = usePostManagerAccess();
  const pendingTaskCount = trpc.tasks.getPendingCount.useQuery(undefined, {
    enabled: Boolean(dbUser),
  });
  const emailTasksReadyCount = trpc.leadEmailTasks.readyCount.useQuery(
    undefined,
    { enabled: Boolean(dbUser) },
  );
  const combinedPendingCount =
    (pendingTaskCount.data ?? 0) + (emailTasksReadyCount.data ?? 0);

  if (!useDashboardTopBar && !forceShowNewCampaignButton) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {(forceShowNewCampaignButton || useDashboardTopBar) && (
        <Button
          variant="secondary"
          onClick={() => router.push(LinkRouter.routeForDataEntry())}
        >
          New Campaign
        </Button>
      )}
      {useDashboardTopBar && (
        <>
          <NavigationMenu>
            <NavigationMenu.List>
              <NavigationMenu.Item>
                <NavigationMenu.NavLink href={LinkRouter.routeForDashboard()}>
                  Dashboard
                </NavigationMenu.NavLink>
              </NavigationMenu.Item>

              <NavigationMenu.Item>
                <NavigationMenu.NavLink
                  href={LinkRouter.routeForPersonalAnalytics()}
                  iconLeft={<BarChart3 />}
                >
                  Analytics
                </NavigationMenu.NavLink>
              </NavigationMenu.Item>

              <NavigationMenu.Item>
                <NavigationMenu.NavLink
                  href={LinkRouter.routeForTasks()}
                  iconLeft={<CheckSquare />}
                  badge={
                    combinedPendingCount > 0 ? combinedPendingCount : undefined
                  }
                >
                  Tasks
                </NavigationMenu.NavLink>
              </NavigationMenu.Item>

              {postManagerAccess.isFetched &&
                postManagerAccess.showPostManager && (
                  <NavigationMenu.Item>
                    <NavigationMenu.NavLink
                      href={LinkRouter.routeForPostManager()}
                      iconLeft={<NewspaperIcon />}
                    >
                      Post Manager
                    </NavigationMenu.NavLink>
                  </NavigationMenu.Item>
                )}
            </NavigationMenu.List>
          </NavigationMenu>

          {useCanSendReferrals.isFetched && useCanSendReferrals.data && (
            <Button
              variant="outline"
              onClick={() =>
                referralDialog.open(
                  {},
                  {
                    title: null,
                    width: 'screen-xl',
                    hideCloseButton: true,
                  },
                )
              }
            >
              <div className="flex gap-2">
                <Coins size={16} />
                <span>Refer & Earn</span>
              </div>
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardNavigation;
